import { Component } from '@theme/component';
import { ThemeEvents, OverlayOpenedEvent, eventBus } from '@theme/events';

/**
 * @typedef {Object} MegaMenuRefs
 * @property {HTMLElement[]} triggers - Mega menu trigger elements
 */

/**
 * Mega Menu component for desktop navigation.
 *
 * Each instance wraps a single trigger + panel. Coordination between
 * sibling instances (close others on open) is handled via a static
 * class-level reference to the currently active instance.
 *
 * Features:
 * - Instant show/hide on hover (no animations)
 * - Small delay on close to prevent accidental closure
 * - Keyboard navigation support (Escape to close)
 * - Focus management with ARIA attributes
 * - Cross-instance coordination (only one panel open at a time)
 *
 * @extends {Component<MegaMenuRefs>}
 */
class MegaMenu extends Component {
	/** @type {MegaMenu | null} Currently active instance across all menus */
	static #activeInstance = null;

	/** @type {number | null} */
	#closeTimeout = null;

	/** @type {number} Close delay in ms to prevent accidental closure */
	#closeDelay = 100;

	/** @type {AbortController | null} */
	#abortController = null;

	/**
	 * The panel was just opened by a touch pointer landing on the trigger.
	 * When the trigger is a link (it has a URL), that first tap must open the
	 * panel, not navigate; the next tap navigates.
	 */
	#openedByTouch = false;

	connectedCallback() {
		super.connectedCallback();
		this.#abortController = new AbortController();
		const { signal } = this.#abortController;

		// Mutual exclusion: close when another surface opens
		document.addEventListener(ThemeEvents.overlayOpened, (e) => {
			if (e.detail?.source !== this && MegaMenu.#activeInstance === this) {
				this.close();
			}
		}, { signal });

		if (this.querySelector('[data-always-open]')) {
			this.open();
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController?.abort();
		this.#abortController = null;
		if (this.#closeTimeout) {
			clearTimeout(this.#closeTimeout);
		}
		if (MegaMenu.#activeInstance === this) {
			MegaMenu.#activeInstance = null;
		}
	}

	/**
	 * Open this instance's panel
	 */
	open() {
		if (this.#closeTimeout) {
			clearTimeout(this.#closeTimeout);
			this.#closeTimeout = null;
		}

		// Close any other open instance
		if (MegaMenu.#activeInstance && MegaMenu.#activeInstance !== this) {
			MegaMenu.#activeInstance.close();
		}

		// Stamp deferred panel template on first open (LCP optimization)
		const tpl = this.querySelector('template[data-mega-menu-panel]');
		if (tpl) {
			this.appendChild(tpl.content.cloneNode(true));
			tpl.remove();
		}

		// Position panel based on mode
		const panel = this.querySelector('.mega-menu__panel');
		if (panel) {
			const rect = this.getBoundingClientRect();
			const useHeaderTop = this.dataset.mode === 'mega' || this.hasAttribute('data-align-header');
			if (useHeaderTop) {
				const header = this.closest('.header') || this.closest('.shopify-section');
				if (header) {
					panel.style.top = header.getBoundingClientRect().bottom + 'px';
				}
			} else {
				panel.style.top = rect.bottom + 'px';
			}
			if (this.dataset.mode !== 'mega') {
				panel.style.left = rect.left + 'px';
			} else {
				panel.style.left = '0px';
			}
		}

		const link = this.querySelector('[aria-haspopup]');
		if (link) {
			link.setAttribute('aria-expanded', 'true');
		}

		this.setAttribute('data-active', '');
		MegaMenu.#activeInstance = this;

		// Notify other surfaces to close (mutual exclusion)
		eventBus.emit(new OverlayOpenedEvent({ source: this }));
	}

	/**
	 * Close this instance's panel
	 */
	close() {
		if (this.querySelector('[data-always-open]')) return;
		const link = this.querySelector('[aria-haspopup]');
		if (link) {
			link.setAttribute('aria-expanded', 'false');
		}

		this.removeAttribute('data-active');
		this.#openedByTouch = false;

		if (MegaMenu.#activeInstance === this) {
			MegaMenu.#activeInstance = null;
		}
	}

	/**
	 * Handle pointerenter on trigger — bound via on:pointerenter="/handleTriggerEnter"
	 * @param {PointerEvent} event
	 */
	handleTriggerEnter(event) {
		const wasActive = this.hasAttribute('data-active');
		this.open();
		if (event?.pointerType === 'touch' && !wasActive) {
			this.#openedByTouch = true;
		}
	}

	/**
	 * Handle click on the trigger — bound via on:click="/handleTriggerClick".
	 * Triggers with a URL render as <a> (ANT-88: "Shop" → new-in, "World" →
	 * magazin). With a mouse, hover already opened the panel and the click
	 * navigates. With touch (tablets show this header from 768 px), the
	 * pointerenter of the first tap opens the panel and that tap's click is
	 * swallowed; a second tap on the open trigger navigates.
	 * @param {MouseEvent} event
	 */
	handleTriggerClick(event) {
		if (!this.#openedByTouch) return;
		this.#openedByTouch = false;
		// The component base proxies `event.target` to this element; the real
		// click target (trigger link vs. a link inside the open panel) comes
		// from the composed path.
		const origin = event.composedPath?.()[0];
		const link = origin instanceof Element ? origin.closest('a.mega-menu__trigger') : null;
		if (link && this.contains(link)) {
			event.preventDefault();
		}
	}

	/**
	 * Handle pointerleave on trigger — bound via on:pointerleave="/handleTriggerLeave"
	 * @param {PointerEvent} _event
	 */
	handleTriggerLeave(event) {
		const related = event.relatedTarget;
		// Stay open if pointer moved to another element within the header
		const header = this.closest('.header');
		if (header && related instanceof Node && header.contains(related)) {
			return;
		}
		this.#closeTimeout = setTimeout(() => {
			this.close();
		}, this.#closeDelay);
	}

	/**
	 * Handle focus on trigger — bound via on:focus="/handleTriggerFocus"
	 * @param {FocusEvent} _event
	 */
	handleTriggerFocus(_event) {
		this.open();
	}

	/**
	 * Handle keydown events — bound via on:keydown="/handleKeydown"
	 * @param {KeyboardEvent} event
	 */
	handleKeydown(event) {
		if (event.key === 'Escape' && MegaMenu.#activeInstance === this) {
			event.preventDefault();
			this.close();

			// Return focus to the trigger link
			const link = this.querySelector('[aria-haspopup]');
			if (link instanceof HTMLElement) {
				link.focus();
			}
		}
	}
}

if (!customElements.get('mega-menu')) {
	customElements.define('mega-menu', MegaMenu);
}
